import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable, timer, of, ReplaySubject } from 'rxjs';
import { tap, map, take, switchMap, filter, first } from 'rxjs/operators';

import { cloneDeep, isEqual } from 'lodash-es';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { BillsRepository, UsersRepository } from '@app/data-layer/repositories';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { fullOrganizationFormFields } from '@constants/organization-validation-keys';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { PaginatedResultModel } from '@app/data-layer/models/common';
import { BillModel } from '@app/data-layer/models/payments';
import { DEFAULT_MAX_POLLING_RETRY, DEFAULT_POLLING_TIMEOUT } from '@constants/constants';
import { enumPaymentState } from '@app/data-layer/enums/payments';
import { CompaniesService } from './companies.service';
import { OrganizationDetailedManagementModel } from '@app/data-layer/models/management/management-organization-detailed-model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  public organizationChange$: BehaviorSubject<{ previous: OrganizationModel; current: OrganizationModel }>;
  public organizationsChange$: BehaviorSubject<{ previous: OrganizationModel[]; current: OrganizationModel[] }>;
  public organizationUpdated$: Subject<void> = new Subject<void>();
  public organizationFailedBill$: ReplaySubject<BillModel> = new ReplaySubject<BillModel>();

  private _organizations: OrganizationModel[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private billsRepository: BillsRepository,
    private companiesService: CompaniesService,
    private organizationsRepository: OrganizationsRepository,
    private usersRepository: UsersRepository,
  ) {
    this.organizationChange$ = new BehaviorSubject({ previous: null, current: null });
    this.organizationsChange$ = new BehaviorSubject({ previous: null, current: [] });
  }

  public getOrganizationAddressLine(organization: OrganizationModel | OrganizationDetailedManagementModel): string {
    return [
      [organization?.address?.line1, organization?.address?.line2, organization?.address?.line3].join(' '),
      organization?.address?.city,
      [organization?.address?.state?.code, organization?.address?.postalCode].join(' '),
      organization?.address?.country?.description,
    ]
      .filter((item: string) => !!item?.trim())
      .join(', ');
  }

  public getOrganizationById(organizationId: number): OrganizationModel {
    return this.organizations.find((organization: OrganizationModel) => organization.id === organizationId);
  }

  public getOrganization(): OrganizationModel {
    return cloneDeep(this.organization);
  }

  public getOrganizationAsync(
    forceUpdate: boolean = false,
    isShowLoader: boolean = false,
    organizationId?: number,
  ): Observable<OrganizationModel> {
    if (!!this.organization && !forceUpdate) {
      return of(cloneDeep(this.organization));
    }
    return this.organizationsRepository.getOrganizations(isShowLoader).pipe(
      tap((res: OrganizationModel[]) => {
        this.organizations = res;
      }),
      map(() => (!!organizationId ? this.getOrganizationById(organizationId) : this.organization)),
    );
  }

  public getOrganizationByIdFromServer(organizationId: number): Observable<OrganizationModel> {
    return this.organizationsRepository.getOrganizationById(organizationId);
  }

  public getOrganizations(): OrganizationModel[] {
    return cloneDeep(this.organizations);
  }

  public getOrganizationsFromServer(): Observable<OrganizationModel[]> {
    return this.organizationsRepository.getOrganizations().pipe(
      tap((organizations: OrganizationModel[]) => {
        this.organizations = organizations;
      }),
    );
  }

  public updateOrganization(organization: OrganizationModel): Observable<OrganizationModel> {
    return this.organizationsRepository.update(organization.id, organization);
  }

  public setDefaultOrganization(organization: OrganizationModel, callback?: () => void): void {
    this.organization = organization;
    if (callback) {
      callback();
    }
  }

  public switchOrganization(organization: OrganizationModel): Observable<void> {
    const previous: OrganizationModel = cloneDeep(this.organization);
    return this.usersRepository.setUserCurrentOrganization(organization.id).pipe(
      switchMap(() => this.companiesService.getUserData(true)),
      tap(() => {
        this.organizationChange$.next({ previous, current: this.organization });
      }),
      map(() => undefined),
    );
  }

  public clearStateOrganization(): void {
    this.organization = null;
    this.organizations = [];
  }

  public createOrganizationForm(_organization?: OrganizationModel): UntypedFormGroup {
    const organization: OrganizationModel = !!_organization ? _organization : this.organization;
    const organizationForm: UntypedFormGroup = this.fb.group({});
    fullOrganizationFormFields.forEach((field: { key: string; validators: ValidatorFn[]; children?: any }) => {
      if (field.children) {
        const childFormGroup: UntypedFormGroup = this.fb.group({});
        field.children.forEach((child: { key: string; validators: ValidatorFn[]; children?: any }) => {
          childFormGroup.addControl(
            child.key,
            this.fb.control(!!organization[field.key] ? organization[field.key][child.key] : null, child.validators),
          );
        });
        organizationForm.addControl(field.key, childFormGroup);
      } else {
        organizationForm.addControl(field.key, this.fb.control(organization[field.key], field.validators));
      }
    });
    return organizationForm;
  }

  public organizationPayBill(billId: number, type?: enumCompanyPaymentType): Observable<PaginatedResultModel<BillModel>> {
    return this.billsRepository.createPaymentIntent(billId, true, true).pipe(
      switchMap(() => {
        return timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
          take(DEFAULT_MAX_POLLING_RETRY),
          switchMap(() => this.billsRepository.search(this.organization.id, { ...(!!type && { type }) }, true)),
          filter(
            (result: PaginatedResultModel<BillModel>) =>
              (result?.items || []).find((item: BillModel) => item.id === billId)?.state !== enumPaymentState.DELAYED,
          ),
          first(),
        );
      }),
      switchMap(() => this.billsRepository.search(this.organization.id, { ...(!!type && { type }) }, true)),
    );
  }

  public deleteDefaultPaymentMethod(organizationId: number, isShowLoader: boolean): Observable<OrganizationModel> {
    return this.organizationsRepository.deleteDefaultPaymentMethod(organizationId, isShowLoader);
  }

  public getBrokerPoaFileUrl(organizationId: number): Observable<string> {
    return this.organizationsRepository.getBrokerPoaFile(organizationId).pipe(map((res: { poaTemplateUrl: string }) => res.poaTemplateUrl));
  }

  private get organization(): OrganizationModel {
    const defaultOrganizationId: number = this.companiesService.getUser()?.defaultOrganizationId;
    return defaultOrganizationId
      ? this.organizations.find((organization: OrganizationModel) => organization.id === defaultOrganizationId)
      : this.organizations[0];
  }

  private set organization(organization: OrganizationModel) {
    const previous: OrganizationModel = cloneDeep(this.organization);
    if (organization && organization.id) {
      this.usersRepository
        .setUserCurrentOrganization(organization.id)
        .pipe(
          switchMap(() => this.companiesService.getUserData(true)),
          take(1),
        )
        .subscribe(() => {
          this.organizationChange$.next({ previous, current: this.organization });
        });
    }
  }

  private get organizations(): OrganizationModel[] {
    return this._organizations;
  }

  private set organizations(organizations: OrganizationModel[]) {
    const previous: OrganizationModel[] = cloneDeep(this._organizations);
    const previousOrganization: OrganizationModel = cloneDeep(this.organization);
    this._organizations = organizations || [];
    this.organizationsChange$.next({
      previous,
      current: this._organizations,
    });
    if (!isEqual(previousOrganization, this.organization)) {
      this.organizationChange$.next({ previous: previousOrganization, current: this.organization });
    }
  }
}
